import React, { forwardRef } from 'react';
import { Alert, Button, Card, Input, Typography } from 'antd';
// Components
import EstateList from '../EstateList/EstateList';
import OwnershipChecksList from '../OwnershipChecksList/OwnershipChecksList';
// Helpers
import { formatEstateSummary } from 'helpers/teams/cbTeamOwnershipTransfer.helpers';
// Types
import {
  AgencyRow,
  CheckResult,
  OwnershipTransferPreview,
} from 'helpers/teams/cbTeamOwnershipTransfer.types';
// Styles
import styles from './TransferPreviewSection.module.scss';

const { Title, Text } = Typography;
const { TextArea } = Input;

type TransferPreviewSectionProps = {
  selectedTarget: AgencyRow | null;
  preview: OwnershipTransferPreview | null;
  checks: CheckResult[];
  reason: string;
  previewLoading: boolean;
  transferLoading: boolean;
  transferCompleted: boolean;
  onReasonChange: (reason: string) => void;
  onRefreshPreview: () => void;
  onTransfer: () => void;
};

const TransferPreviewSection = forwardRef<
  HTMLDivElement,
  TransferPreviewSectionProps
>(
  (
    {
      selectedTarget,
      preview,
      checks,
      reason,
      previewLoading,
      transferLoading,
      transferCompleted,
      onReasonChange,
      onRefreshPreview,
      onTransfer,
    },
    ref
  ): JSX.Element => {
    const failedChecks = checks.filter((check) => !check.passed);
    const canTransfer = Boolean(preview?.canTransfer) && !transferCompleted;

    return (
      <div ref={ref}>
        <Card title="Transfer preview" style={{ marginTop: 16 }}>
          {selectedTarget && (
            <div className={styles.previewTarget}>
              <Text strong className={styles.previewTargetTitle}>
                New owner
              </Text>
              <div className={styles.sourceMeta}>
                <div>
                  Name:
                  <span className={styles.value}>
                    {selectedTarget.name || '—'}
                  </span>
                </div>
                <div>
                  Email:
                  <span className={styles.value}>
                    {selectedTarget.email || '—'}
                  </span>
                </div>
                <div>
                  Company:
                  <span className={styles.value}>
                    {selectedTarget.companyName || '—'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {failedChecks.length > 0 ? (
            <Alert
              type="error"
              showIcon
              message={`Ownership transfer blocked (${
                failedChecks.length
              } failed check${failedChecks.length === 1 ? '' : 's'})`}
              description="Fix every failed check below before transferring."
            />
          ) : (
            <Alert
              type="success"
              showIcon
              message="All checks passed — transfer can proceed."
            />
          )}

          <Title level={5} style={{ marginTop: 16 }}>
            Checks
          </Title>
          <OwnershipChecksList checks={checks} />

          {preview?.estate && (
            <>
              <Title level={5} style={{ marginTop: 16 }}>
                What will move
              </Title>
              <Text type="secondary">
                {formatEstateSummary(preview.estate)}
              </Text>
              <EstateList estate={preview.estate} />
            </>
          )}

          <div className={styles.reasonField}>
            <Text strong>Reason (optional)</Text>
            <TextArea
              rows={3}
              value={reason}
              onChange={(event) => onReasonChange(event.target.value)}
              placeholder="Why this transfer was approved (saved in the transfer log)"
              disabled={transferCompleted}
              style={{ marginTop: 8 }}
            />
          </div>

          <div className={styles.actions}>
            <Button
              onClick={onRefreshPreview}
              loading={previewLoading}
              disabled={!selectedTarget || transferCompleted}
            >
              Refresh preview
            </Button>
            <Button
              type="primary"
              danger
              onClick={onTransfer}
              loading={transferLoading}
              disabled={!canTransfer || !selectedTarget}
            >
              Transfer ownership
            </Button>
          </div>
        </Card>
      </div>
    );
  }
);

TransferPreviewSection.displayName = 'TransferPreviewSection';

export default TransferPreviewSection;
